import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  getManufacturers, createManufacturer, updateManufacturer, deleteManufacturer,
  getProductLines, createProductLine, updateProductLine, deleteProductLine,
  getPricingTemplates, createPricingTemplate, updatePricingTemplate, deletePricingTemplate,
  getUser, logout
} from '@/lib/api';
import { ArrowLeft, Zap, Settings, LogOut, Plus, Edit2, Trash2, Package, DollarSign, Factory, Loader2 } from 'lucide-react';
import Logo from '@/components/Logo';

const OPENING_TYPES = ['Single Hung', 'Horizontal Roller', 'Picture Window', 'Casement', 'Entry Door', 'French Door', 'Sliding Glass Door', 'Storefront / Commercial'];

export default function AdminSettings() {
  const navigate = useNavigate();
  const user = getUser();
  const [manufacturers, setManufacturers] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [pricingTemplates, setPricingTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showMfrDialog, setShowMfrDialog] = useState(false);
  const [editMfr, setEditMfr] = useState(null);
  const [mfrForm, setMfrForm] = useState({ name: '', notes: '', warranty_info: '', active: true });

  const [showPlDialog, setShowPlDialog] = useState(false);
  const [editPl, setEditPl] = useState(null);
  const [plForm, setPlForm] = useState({ manufacturer_id: '', name: '', category: 'windows', lead_time_days: 0, notes: '', active: true });

  const [showPtDialog, setShowPtDialog] = useState(false);
  const [editPt, setEditPt] = useState(null);
  const [ptForm, setPtForm] = useState({ manufacturer_id: '', product_line_id: '', opening_type: '', base_price: 0, labor_price: 0, permit_allowance: 0, finish_work_allowance: 0, active: true });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [m, p, pt] = await Promise.all([getManufacturers(), getProductLines(), getPricingTemplates()]);
      setManufacturers(m.data);
      setProductLines(p.data);
      setPricingTemplates(pt.data);
    } catch (err) {
      toast.error('Failed to load settings');
    }
    setLoading(false);
  };

  // Manufacturer CRUD
  const openMfrCreate = () => { setEditMfr(null); setMfrForm({ name: '', notes: '', warranty_info: '', active: true }); setShowMfrDialog(true); };
  const openMfrEdit = (m) => { setEditMfr(m); setMfrForm({ name: m.name, notes: m.notes || '', warranty_info: m.warranty_info || '', active: m.active }); setShowMfrDialog(true); };
  const saveMfr = async () => {
    try {
      if (editMfr) await updateManufacturer(editMfr.id, mfrForm);
      else await createManufacturer(mfrForm);
      toast.success(editMfr ? 'Manufacturer updated' : 'Manufacturer created');
      setShowMfrDialog(false);
      loadAll();
    } catch (err) { toast.error('Failed to save'); }
  };
  const removeMfr = async (id) => {
    if (!window.confirm('Delete this manufacturer? This will affect related product lines and pricing.')) return;
    try { await deleteManufacturer(id); toast.success('Deleted'); loadAll(); } catch (err) { toast.error('Failed'); }
  };

  // Product Line CRUD
  const openPlCreate = () => { setEditPl(null); setPlForm({ manufacturer_id: manufacturers[0]?.id || '', name: '', category: 'windows', lead_time_days: 0, notes: '', active: true }); setShowPlDialog(true); };
  const openPlEdit = (p) => { setEditPl(p); setPlForm({ manufacturer_id: p.manufacturer_id, name: p.name, category: p.category, lead_time_days: p.lead_time_days, notes: p.notes || '', active: p.active }); setShowPlDialog(true); };
  const savePl = async () => {
    try {
      if (editPl) await updateProductLine(editPl.id, plForm);
      else await createProductLine(plForm);
      toast.success(editPl ? 'Product line updated' : 'Product line created');
      setShowPlDialog(false);
      loadAll();
    } catch (err) { toast.error('Failed to save'); }
  };
  const removePl = async (id) => {
    if (!window.confirm('Delete this product line?')) return;
    try { await deleteProductLine(id); toast.success('Deleted'); loadAll(); } catch (err) { toast.error('Failed'); }
  };

  // Pricing Template CRUD
  const openPtCreate = () => { setEditPt(null); setPtForm({ manufacturer_id: manufacturers[0]?.id || '', product_line_id: '', opening_type: OPENING_TYPES[0], base_price: 0, labor_price: 0, permit_allowance: 0, finish_work_allowance: 0, active: true }); setShowPtDialog(true); };
  const openPtEdit = (p) => { setEditPt(p); setPtForm({ manufacturer_id: p.manufacturer_id, product_line_id: p.product_line_id, opening_type: p.opening_type, base_price: p.base_price, labor_price: p.labor_price, permit_allowance: p.permit_allowance, finish_work_allowance: p.finish_work_allowance, active: p.active }); setShowPtDialog(true); };
  const savePt = async () => {
    try {
      if (editPt) await updatePricingTemplate(editPt.id, ptForm);
      else await createPricingTemplate(ptForm);
      toast.success(editPt ? 'Template updated' : 'Template created');
      setShowPtDialog(false);
      loadAll();
    } catch (err) { toast.error('Failed to save'); }
  };
  const removePt = async (id) => {
    if (!window.confirm('Delete this pricing template?')) return;
    try { await deletePricingTemplate(id); toast.success('Deleted'); loadAll(); } catch (err) { toast.error('Failed'); }
  };

  const getMfrName = (id) => manufacturers.find(m => m.id === id)?.name || '-';
  const getPlName = (id) => productLines.find(p => p.id === id)?.name || '-';

  if (loading) return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-[var(--brand-ocean-700)]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="admin-settings">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[hsl(var(--border))]">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-[var(--brand-slate-600)] hover:text-[var(--brand-ocean-700)]">
              <ArrowLeft className="h-4 w-4 mr-1" /> Dashboard
            </Button>
            <Separator orientation="vertical" className="h-5" />
            <span className="font-heading font-semibold text-sm flex items-center gap-2 text-[var(--brand-slate-900)]"><Settings className="h-4 w-4 text-[var(--brand-ocean-700)]" /> Settings</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--brand-slate-600)] hidden sm:inline">{user?.email}</span>
            <Separator orientation="vertical" className="h-5" />
            <Button variant="ghost" size="sm" onClick={logout} className="text-red-500 hover:text-red-600 hover:bg-red-50">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-6">
        <Tabs defaultValue="manufacturers">
          <TabsList className="mb-4 h-11">
            <TabsTrigger value="branding" className="gap-1.5"><Zap className="h-4 w-4" /> Branding</TabsTrigger>
            <TabsTrigger value="manufacturers" className="gap-1.5"><Factory className="h-4 w-4" /> Manufacturers</TabsTrigger>
            <TabsTrigger value="product-lines" className="gap-1.5"><Package className="h-4 w-4" /> Product Lines</TabsTrigger>
            <TabsTrigger value="pricing" className="gap-1.5"><DollarSign className="h-4 w-4" /> Pricing Templates</TabsTrigger>
          </TabsList>

          {/* Branding */}
          <TabsContent value="branding">
            <div className="max-w-2xl">
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)] mb-4">Company Branding</h2>
              <Card className="rounded-2xl border-2">
                <CardContent className="p-6 space-y-6">
                  <div>
                    <h3 className="font-heading font-semibold text-sm mb-3">Logo Preview</h3>
                    <div className="flex gap-4 items-center">
                      <div className="p-4 bg-white rounded-xl border-2">
                        <Logo variant="light" size="default" />
                      </div>
                      <div className="p-4 bg-[var(--brand-slate-900)] rounded-xl border-2">
                        <Logo variant="dark" size="default" />
                      </div>
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <h3 className="font-heading font-semibold text-sm mb-2">Logo Files</h3>
                    <p className="text-xs text-[var(--brand-slate-600)] mb-3">Current logos are stored in /app/frontend/public/logos/</p>
                    <div className="bg-[hsl(var(--secondary))]/50 rounded-xl p-4 text-sm">
                      <p className="font-medium text-[var(--brand-slate-900)] mb-2">To update your logo:</p>
                      <ol className="list-decimal list-inside space-y-1 text-xs text-[var(--brand-slate-600)]">
                        <li>Replace logo files in: <code className="bg-white px-1 py-0.5 rounded font-mono">/app/frontend/public/logos/</code></li>
                        <li>Supported files: logo-light-bg.png (for light backgrounds), logo-white.png (for dark backgrounds)</li>
                        <li>Recommended size: 200px height, transparent background</li>
                        <li>Refresh the page to see changes</li>
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Manufacturers */}
          <TabsContent value="manufacturers">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)]">Manufacturers</h2>
              <Button onClick={openMfrCreate} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-9 text-sm font-semibold">
                <Plus className="h-4 w-4 mr-1" /> Add Manufacturer
              </Button>
            </div>
            <Card className="rounded-2xl overflow-hidden border-2">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[hsl(var(--secondary))]">
                    <TableHead className="font-heading font-semibold">Name</TableHead>
                    <TableHead className="font-heading font-semibold">Warranty</TableHead>
                    <TableHead className="font-heading font-semibold">Status</TableHead>
                    <TableHead className="w-24 font-heading font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {manufacturers.map(m => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium text-[var(--brand-slate-900)]">{m.name}</TableCell>
                      <TableCell className="text-sm text-[var(--brand-slate-600)]">{m.warranty_info || '-'}</TableCell>
                      <TableCell><Badge variant={m.active ? 'default' : 'secondary'} className={m.active ? 'bg-green-100 text-green-700 border border-green-200' : ''}>{m.active ? 'Active' : 'Inactive'}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openMfrEdit(m)} className="h-8 hover:bg-[var(--brand-ocean-100)]">
                            <Edit2 className="h-3.5 w-3.5 text-[var(--brand-ocean-700)]" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removeMfr(m.id)} className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {manufacturers.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-[var(--brand-slate-600)]">No manufacturers yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Product Lines */}
          <TabsContent value="product-lines">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)]">Product Lines</h2>
              <Button onClick={openPlCreate} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-9 text-sm font-semibold">
                <Plus className="h-4 w-4 mr-1" /> Add Product Line
              </Button>
            </div>
            <Card className="rounded-2xl overflow-hidden border-2">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[hsl(var(--secondary))]">
                    <TableHead className="font-heading font-semibold">Name</TableHead>
                    <TableHead className="font-heading font-semibold">Manufacturer</TableHead>
                    <TableHead className="font-heading font-semibold">Category</TableHead>
                    <TableHead className="font-heading font-semibold">Lead Time</TableHead>
                    <TableHead className="font-heading font-semibold">Status</TableHead>
                    <TableHead className="w-24 font-heading font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productLines.map(p => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium text-[var(--brand-slate-900)]">{p.name}</TableCell>
                      <TableCell className="text-[var(--brand-slate-600)]">{getMfrName(p.manufacturer_id)}</TableCell>
                      <TableCell className="capitalize text-[var(--brand-slate-600)]">{p.category}</TableCell>
                      <TableCell className="text-[var(--brand-slate-600)]">{p.lead_time_days} days</TableCell>
                      <TableCell><Badge variant={p.active ? 'default' : 'secondary'} className={p.active ? 'bg-green-100 text-green-700 border border-green-200' : ''}>{p.active ? 'Active' : 'Inactive'}</Badge></TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openPlEdit(p)} className="h-8 hover:bg-[var(--brand-ocean-100)]">
                            <Edit2 className="h-3.5 w-3.5 text-[var(--brand-ocean-700)]" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => removePl(p.id)} className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {productLines.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-[var(--brand-slate-600)]">No product lines yet</TableCell></TableRow>}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Pricing Templates */}
          <TabsContent value="pricing">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-xl font-semibold text-[var(--brand-slate-900)]">Pricing Templates</h2>
              <Button onClick={openPtCreate} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-9 text-sm font-semibold">
                <Plus className="h-4 w-4 mr-1" /> Add Template
              </Button>
            </div>
            <Card className="rounded-2xl overflow-hidden border-2">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[hsl(var(--secondary))]">
                      <TableHead className="font-heading font-semibold">Manufacturer</TableHead>
                      <TableHead className="font-heading font-semibold">Opening Type</TableHead>
                      <TableHead className="font-heading font-semibold">Base $</TableHead>
                      <TableHead className="font-heading font-semibold">Labor $</TableHead>
                      <TableHead className="font-heading font-semibold">Permit $</TableHead>
                      <TableHead className="font-heading font-semibold">Finish $</TableHead>
                      <TableHead className="font-heading font-semibold">Status</TableHead>
                      <TableHead className="w-24 font-heading font-semibold">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pricingTemplates.map(p => (
                      <TableRow key={p.id}>
                        <TableCell className="text-[var(--brand-slate-600)]">{getMfrName(p.manufacturer_id)}</TableCell>
                        <TableCell className="text-[var(--brand-slate-900)]">{p.opening_type}</TableCell>
                        <TableCell className="font-mono text-[var(--brand-slate-900)]">${p.base_price}</TableCell>
                        <TableCell className="font-mono text-[var(--brand-slate-600)]">${p.labor_price}</TableCell>
                        <TableCell className="font-mono text-[var(--brand-slate-600)]">${p.permit_allowance}</TableCell>
                        <TableCell className="font-mono text-[var(--brand-slate-600)]">${p.finish_work_allowance}</TableCell>
                        <TableCell><Badge variant={p.active ? 'default' : 'secondary'} className={p.active ? 'bg-green-100 text-green-700 border border-green-200' : ''}>{p.active ? 'Active' : 'Inactive'}</Badge></TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openPtEdit(p)} className="h-8 hover:bg-[var(--brand-ocean-100)]">
                              <Edit2 className="h-3.5 w-3.5 text-[var(--brand-ocean-700)]" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => removePt(p.id)} className="h-8 text-red-500 hover:text-red-600 hover:bg-red-50">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {pricingTemplates.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-[var(--brand-slate-600)]">No pricing templates yet</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Manufacturer Dialog */}
      <Dialog open={showMfrDialog} onOpenChange={setShowMfrDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editMfr ? 'Edit' : 'Add'} Manufacturer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={mfrForm.name} onChange={e => setMfrForm(p => ({...p, name: e.target.value}))} className="mt-1.5 border-2" /></div>
            <div><Label>Warranty Info</Label><Input value={mfrForm.warranty_info} onChange={e => setMfrForm(p => ({...p, warranty_info: e.target.value}))} className="mt-1.5 border-2" placeholder="e.g., 10-year limited warranty" /></div>
            <div><Label>Notes (Optional)</Label><Textarea value={mfrForm.notes} onChange={e => setMfrForm(p => ({...p, notes: e.target.value}))} rows={2} className="mt-1.5 border-2" /></div>
            <div className="flex items-center gap-3 pt-2">
              <Switch checked={mfrForm.active} onCheckedChange={v => setMfrForm(p => ({...p, active: v}))} />
              <Label className="font-normal">Active</Label>
            </div>
            <Button onClick={saveMfr} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-10 font-semibold">Save Manufacturer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Product Line Dialog */}
      <Dialog open={showPlDialog} onOpenChange={setShowPlDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editPl ? 'Edit' : 'Add'} Product Line</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Manufacturer</Label>
              <Select value={plForm.manufacturer_id} onValueChange={v => setPlForm(p => ({...p, manufacturer_id: v}))}>
                <SelectTrigger className="mt-1.5 border-2"><SelectValue /></SelectTrigger>
                <SelectContent>{manufacturers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Name</Label><Input value={plForm.name} onChange={e => setPlForm(p => ({...p, name: e.target.value}))} className="mt-1.5 border-2" placeholder="e.g., Mr Glass or ES Windows" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={plForm.category} onValueChange={v => setPlForm(p => ({...p, category: v}))}>
                  <SelectTrigger className="mt-1.5 border-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="windows">Windows</SelectItem>
                    <SelectItem value="doors">Doors</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Lead Time (days)</Label><Input type="number" value={plForm.lead_time_days} onChange={e => setPlForm(p => ({...p, lead_time_days: parseInt(e.target.value) || 0}))} className="mt-1.5 border-2" /></div>
            </div>
            <div><Label>Notes (Optional)</Label><Textarea value={plForm.notes} onChange={e => setPlForm(p => ({...p, notes: e.target.value}))} rows={2} className="mt-1.5 border-2" /></div>
            <div className="flex items-center gap-3 pt-2">
              <Switch checked={plForm.active} onCheckedChange={v => setPlForm(p => ({...p, active: v}))} />
              <Label className="font-normal">Active</Label>
            </div>
            <Button onClick={savePl} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-10 font-semibold">Save Product Line</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pricing Template Dialog */}
      <Dialog open={showPtDialog} onOpenChange={setShowPtDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editPt ? 'Edit' : 'Add'} Pricing Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Manufacturer</Label>
              <Select value={ptForm.manufacturer_id} onValueChange={v => setPtForm(p => ({...p, manufacturer_id: v}))}>
                <SelectTrigger className="mt-1.5 border-2"><SelectValue /></SelectTrigger>
                <SelectContent>{manufacturers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Opening Type</Label>
              <Select value={ptForm.opening_type} onValueChange={v => setPtForm(p => ({...p, opening_type: v}))}>
                <SelectTrigger className="mt-1.5 border-2"><SelectValue /></SelectTrigger>
                <SelectContent>{OPENING_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Base Price ($)</Label><Input type="number" value={ptForm.base_price} onChange={e => setPtForm(p => ({...p, base_price: parseFloat(e.target.value) || 0}))} className="mt-1.5 border-2" /></div>
              <div><Label>Labor ($)</Label><Input type="number" value={ptForm.labor_price} onChange={e => setPtForm(p => ({...p, labor_price: parseFloat(e.target.value) || 0}))} className="mt-1.5 border-2" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Permit ($)</Label><Input type="number" value={ptForm.permit_allowance} onChange={e => setPtForm(p => ({...p, permit_allowance: parseFloat(e.target.value) || 0}))} className="mt-1.5 border-2" /></div>
              <div><Label>Finish Work ($)</Label><Input type="number" value={ptForm.finish_work_allowance} onChange={e => setPtForm(p => ({...p, finish_work_allowance: parseFloat(e.target.value) || 0}))} className="mt-1.5 border-2" /></div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <Switch checked={ptForm.active} onCheckedChange={v => setPtForm(p => ({...p, active: v}))} />
              <Label className="font-normal">Active</Label>
            </div>
            <Button onClick={savePt} className="w-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-xl h-10 font-semibold">Save Template</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
